import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import OrbitalLoader from "../common/OrbitalLoader";
import GeneratingVideoPreview from "../videos/GeneratingVideoPreview";
import { videosService } from "../../services/videos";
import { parsePaginatedResponse } from "../../utils/pagination";
import { statusLabel } from "../../utils/videos";
import { getApiErrorMessage } from "../../utils/apiErrors";
import { formatDate } from "../../utils/date";
import { useToast } from "../../contexts/ToastContext";

const POLL_MS = 4000;

const formatElapsed = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const getFirstSceneImageUrl = (script) => {
  const scenes = script?.script_data?.scenes;
  if (!Array.isArray(scenes)) return null;
  for (const scene of scenes) {
    if (scene?.image_url) return scene.image_url;
  }
  return null;
};

const ScriptVideoRow = ({ video, selected, onSelect, scenePreviewUrl }) => {
  const isActive = video.status === "pending" || video.status === "generating";
  const generatingPreviewUrl = video.script_preview_url || scenePreviewUrl;
  const thumbUrl = video.thumbnail_url || generatingPreviewUrl;

  return (
    <button
      type="button"
      onClick={() => onSelect(video)}
      className={`w-full text-left rounded-lg border p-3 transition-colors ${
        selected
          ? "border-theme-primary bg-theme-main"
          : "border-theme-secondary bg-theme-secondary hover:bg-theme-main"
      }`}
    >
      <div className="flex items-center gap-3">
        {isActive ? (
          <GeneratingVideoPreview imageUrl={generatingPreviewUrl} compact />
        ) : (
          <div className="relative w-12 flex-shrink-0 aspect-[9/16] rounded overflow-hidden border border-theme-secondary bg-theme-main">
            {thumbUrl ? (
              <img src={thumbUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-[10px] text-theme-text opacity-50 px-1 text-center">
                {statusLabel(video.status)}
              </div>
            )}
          </div>
        )}
        <div className="min-w-0 flex-1 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-theme-text truncate">
              {video.title || "Generated video"}
            </p>
            <p className="text-xs text-theme-text opacity-70 mt-0.5">
              {formatDate(video.create_date)}
            </p>
          </div>
          <span className={`text-xs flex-shrink-0 ${isActive ? "text-theme-primary" : "text-theme-text opacity-70"}`}>
            {statusLabel(video.status)}
          </span>
        </div>
      </div>
    </button>
  );
};

const ScriptVideosPanel = ({
  script,
  showCreateOnMount = false,
  onBack,
  onVideoCreated,
  onClearCreateParam,
}) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(showCreateOnMount);
  const [promptOverride, setPromptOverride] = useState("");
  const [creating, setCreating] = useState(false);
  const [selectedVideoHash, setSelectedVideoHash] = useState(null);
  const startedAtRef = useRef(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const { showToast } = useToast();

  const loadVideos = useCallback(({ silent = false } = {}) => {
    if (!script?.hash) return;
    if (!silent) {
      setLoading(true);
      setLoadError(null);
    }
    videosService.listVideos(
      { script_hash: script.hash, page_size: 50 },
      (response) => {
        const { results } = parsePaginatedResponse(response);
        setVideos(results);
        if (!silent) setLoading(false);
        setSelectedVideoHash((prev) => prev || results[0]?.hash || null);
      },
      () => {
        if (!silent) {
          setLoadError("Could not load videos for this script.");
          setLoading(false);
        }
      },
    );
  }, [script?.hash]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  useEffect(() => {
    if (!showCreateOnMount) return;
    setShowConfirm(true);
  }, [showCreateOnMount]);

  const hasInProgress = videos.some(
    (video) => video.status === "pending" || video.status === "generating",
  );

  useEffect(() => {
    if (!hasInProgress) return undefined;
    const poll = () => loadVideos({ silent: true });
    const interval = setInterval(poll, POLL_MS);
    return () => clearInterval(interval);
  }, [hasInProgress, loadVideos]);

  const selectedVideo = useMemo(
    () => videos.find((video) => video.hash === selectedVideoHash) || null,
    [videos, selectedVideoHash],
  );

  const scenePreviewUrl = useMemo(() => getFirstSceneImageUrl(script), [script]);

  const isGeneratingSelected =
    selectedVideo?.status === "pending" || selectedVideo?.status === "generating";

  useEffect(() => {
    if (!isGeneratingSelected && !creating) {
      startedAtRef.current = null;
      setElapsedSeconds(0);
      return undefined;
    }
    if (!startedAtRef.current) {
      startedAtRef.current = Date.now();
    }
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [isGeneratingSelected, creating, selectedVideo?.hash]);

  const handleConfirmCreate = () => {
    if (!script?.hash || creating) return;
    setCreating(true);
    videosService.createVideo(
      {
        script_hash: script.hash,
        ...(promptOverride.trim() ? { prompt: promptOverride.trim() } : {}),
      },
      (created) => {
        setCreating(false);
        setShowConfirm(false);
        setPromptOverride("");
        onClearCreateParam?.();
        setVideos((prev) => [created, ...prev.filter((item) => item.hash !== created.hash)]);
        setSelectedVideoHash(created.hash);
        onVideoCreated?.();
      },
      (error) => {
        setCreating(false);
        showToast(getApiErrorMessage(error, "Could not start video generation."), {
          type: "error",
        });
      },
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 pt-3 overflow-y-auto">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-theme-primary hover:underline mb-3 text-left"
        >
          Back to script
        </button>
      ) : null}
      {loading ? (
        <div className="py-10 text-center">
          <OrbitalLoader size={64} text="Loading videos..." />
        </div>
      ) : loadError ? (
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 text-theme-text opacity-80">
          {loadError}
        </div>
      ) : showConfirm ? (
        <div className="space-y-4">
          {creating ? (
            <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-8 text-center">
              <OrbitalLoader size={64} text="Starting video generation..." />
              <p className="text-sm text-theme-text opacity-75 mt-3">
                {formatElapsed(elapsedSeconds)} elapsed
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 space-y-3">
                <h2 className="text-lg font-semibold text-theme-text">Generate a video?</h2>
                <p className="text-sm text-theme-text opacity-80">
                  This will render a new video from your script.
                </p>
                <label className="block text-sm text-theme-text">
                  <span className="opacity-80">Prompt (optional)</span>
                  <textarea
                    value={promptOverride}
                    onChange={(event) => setPromptOverride(event.target.value)}
                    rows={3}
                    placeholder="Extra style notes, pacing, or visual direction..."
                    className="mt-1 w-full rounded border border-theme-secondary bg-theme-main px-3 py-2 text-sm text-theme-text resize-y"
                  />
                </label>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirm(false);
                    setPromptOverride("");
                    onClearCreateParam?.();
                    if (!videos.length) onBack();
                  }}
                  className="px-3 py-2 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-main"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmCreate}
                  className="px-3 py-2 rounded bg-theme-primary text-theme-button text-sm font-medium hover:opacity-90"
                >
                  Generate video
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0 gap-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-theme-text opacity-80">
              {videos.length
                ? `${videos.length} video${videos.length === 1 ? "" : "s"} for this script`
                : "No videos yet for this script"}
            </p>
            <button
              type="button"
              onClick={() => {
                onClearCreateParam?.();
                setShowConfirm(true);
              }}
              className="px-3 py-2 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-secondary"
            >
              Create new video
            </button>
          </div>

          {videos.length ? (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,240px)_1fr] gap-4 flex-1 min-h-0">
              <div className="space-y-2 overflow-y-auto max-h-[40vh] lg:max-h-none">
                {videos.map((video) => (
                  <ScriptVideoRow
                    key={video.hash}
                    video={video}
                    selected={video.hash === selectedVideoHash}
                    onSelect={(item) => setSelectedVideoHash(item.hash)}
                    scenePreviewUrl={scenePreviewUrl}
                  />
                ))}
              </div>

              <div className="min-h-0">
                {selectedVideo?.status === "completed" && selectedVideo.video_url ? (
                  <div className="rounded-lg overflow-hidden border border-theme-secondary bg-black">
                    <video
                      src={selectedVideo.video_url}
                      controls
                      playsInline
                      className="w-full max-h-[420px] bg-black"
                      poster={selectedVideo.thumbnail_url || undefined}
                    />
                  </div>
                ) : null}

                {isGeneratingSelected ? (
                  <div className="space-y-3">
                    <GeneratingVideoPreview
                      imageUrl={selectedVideo.script_preview_url || scenePreviewUrl}
                      elapsedSeconds={elapsedSeconds}
                    />
                    <p className="text-xs text-theme-text opacity-60 text-center">
                      Generation continues in the background. Switch back to Script anytime.
                    </p>
                  </div>
                ) : null}

                {selectedVideo?.status === "failed" ? (
                  <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 text-theme-text opacity-80">
                    {selectedVideo.status_error || "Video generation failed."}
                  </div>
                ) : null}

                {selectedVideo?.hash ? (
                  <Link
                    to={`/videos/${encodeURIComponent(selectedVideo.hash)}/`}
                    className="inline-block mt-3 text-sm text-theme-primary hover:underline"
                  >
                    Open in Videos library
                  </Link>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-6 text-center space-y-3">
              <p className="text-theme-text opacity-80">
                Create your first video from this script.
              </p>
              <button
                type="button"
                onClick={() => {
                  onClearCreateParam?.();
                  setShowConfirm(true);
                }}
                className="px-3 py-2 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-main"
              >
                Create video
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScriptVideosPanel;
